/* eslint-disable no-useless-catch */
/* eslint-disable class-methods-use-this */
import { v4 as uuidv4 } from 'uuid';

const GRANT_DEVICE_TOKEN_QUERY = `
  query grantDeviceToken($deviceList: [DeviceCompositeKey]) {
    grantDeviceToken(input: { deviceList: $deviceList }) {
      credential {
        accessKeyId
        secretAccessKey
        sessionToken
      }
      deviceList {
        thingName
        derivant
      }
    }
  }
`;

function apiError(message, props = {}) {
  const err = Object.assign(new Error(message), props);
  err.generalize = () => err;
  return err;
}

class ApiService {
  useAuthService(authService) {
    this.authService = authService;
    return this;
  }

  async requestToIoTServer({ apiName, params = {}, method = 'GET', body }) {
    if (!this.authService) {
      return Promise.reject(new Error('inject auth service at first'));
    }
    const jwtToken = await this.authService.getSessionJwtToken();
    const host = import.meta.env.VITE_BACKEND_DOMAIN;

    return this.requestToCloud({
      apiName,
      params,
      method,
      body,
      host,
      token: jwtToken,
    });
  }

  async requestToAIServer({ apiName, params = {}, method = 'GET', body, headers = {} }) {
    if (!this.authService) {
      return Promise.reject(new Error('inject auth service at first'));
    }
    const jwtToken = await this.authService.getSessionJwtToken();
    const host = import.meta.env.VITE_VORTEXAI_SERVER_URL;
    const headerOptions = {
      'X-Request-ID': uuidv4(),
      ...headers,
    };

    return this.requestToCloud({
      apiName,
      params,
      method,
      body,
      host,
      token: jwtToken,
      headerOptions,
    });
  }

  async grantDeviceToken({ deviceList }) {
    try {
      if (!this.authService) {
        throw apiError('inject auth service at first');
      }
      const jwtToken = await this.authService.getSessionJwtToken();
      const url = import.meta.env.VITE_BACKEND_URL;
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${jwtToken}`,
        },
        body: JSON.stringify({
          query: GRANT_DEVICE_TOKEN_QUERY,
          variables: { deviceList },
        }),
      });
      if (!response.ok) {
        throw apiError(`HTTP error, status = ${response.status}`, { status: response.status });
      }
      const json = await response.json();
      if (json.errors?.length) {
        throw apiError(json.errors[0].message);
      }
      return json;
    } catch (err) {
      // Upstream core/device.fetchGrantDeviceToken does `throw error.generalize()`;
      // wrap any non-apiError (network failure from fetch, JSON parse error from
      // response.json, etc.) so the contract holds.
      if (typeof err.generalize === 'function') {
        throw err;
      }
      throw apiError(err.message, { cause: err });
    }
  }

  /**
   * all the API request from vsaas-ai repo is called from this function, not from vsaas-portal/ApiService.js
   *  except event insight would call graphQL to Vortex.
   */
  async requestToCloud({ apiName, params = {}, method = 'GET', body, host, token, headerOptions = {} }) {
    try {
      const conditions =
        Object.keys(params).length === 0 && params.constructor === Object
          ? ''
          : `?${Object.keys(params)
              .map((key) => `${key}=${params[key]}`)
              .join('&')
              .replace('#', '%23')}`;
      const url = `https://${host}/${apiName}${conditions}`;
      const headers = {
        'content-type': 'application/json',
        Authorization: `Bearer ${token}`,
        ...(Object.entries(headerOptions).length > 0 ? headerOptions : {}),
      };

      const result = await fetch(url, {
        headers,
        method,
        body: JSON.stringify(body),
      })
        .then((response) => {
          if (response.ok) {
            return response.text();
          }
          const error = new Error(`HTTP error, status = ${response.status}`, { cause: response });
          error.status = response.status;
          throw error;
        })
        .then((data) => {
          if (!data || data.trim() === '') {
            return null;
          }
          return JSON.parse(data);
        })
        .then((data) => {
          if (data === null) {
            return null;
          }
          let currentData = data;
          if ('X-Request-ID' in headers) {
            if (Array.isArray(currentData)) {
              currentData.push({ 'X-Request-ID': headers['X-Request-ID'] });
            } else if (typeof currentData === 'string') {
              currentData = JSON.parse(currentData);
              currentData.push({ 'X-Request-ID': headers['X-Request-ID'] });
            } else {
              currentData['X-Request-ID'] = headers['X-Request-ID'];
            }
          }

          return JSON.stringify(currentData);
        });
      const rr = result ? JSON.parse(result) : null;
      return rr;
    } catch (error) {
      throw error;
    }
  }
}

const singleton = new ApiService();
global.__ApiService = singleton;
export default singleton;
export { ApiService };
